import React from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { useProjects } from '../../contexts/ProjectContext';
import { usePostModal } from '../../contexts/PostModalContext';
import {
  LayoutDashboard,
  CalendarDays,
  ClipboardList,
  Image as MediaIcon,
  Plus,
  Bell,
  ChevronDown,
  HelpCircle,
  User,
  Settings as SettingsIcon,
  LogOut,
  FolderKanban,
  Link2,
  CreditCard,
  ShieldCheck,
  AlertTriangle,
  Zap
} from 'lucide-react';
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../ui/avatar";
import { Badge } from "../ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import NotificationBell from '../NotificationBell';
import { API_BASE_URL } from '../../config';
import { cn } from "../../lib/utils";
import GamifiedAvatar from '../GamifiedAvatar';
import UserRPGBadges from '../UserRPGBadges';
import { useGlobalState } from '../../contexts/GlobalStateContext';
import { useSupport } from "../../contexts/SupportContext";
import { brand } from '../../config/brand';
import axios from 'axios';

export default function Navbar(): JSX.Element {
  const location = useLocation();
  const history = useHistory();
  const { projects, activeProject, setActiveProject } = useProjects();
  const { openPostModal } = usePostModal();
  const { user, setUser } = useGlobalState();
  const { openSupport } = useSupport();
  const [resending, setResending] = React.useState(false);

  if (!user) return <div />;

  const getInitials = (name: string) => {
    if (!name) return '??';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0].slice(0, 2).toUpperCase();
  };

  const handleNavigate = (path: string) => {
    history.push(path);
  };

  const isActive = (path: string) => location.pathname === path;

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    history.push('/');
    window.location.reload();
  };

  const handleResendVerification = async () => {
    if (!user?.email) return;
    setResending(true);
    try {
      await axios.post(`${API_BASE_URL}/auth/resend-verification`, { email: user.email });
      alert('Verification email resent! Please check your inbox.');
    } catch (error: any) {
      const msg = error.response?.data?.message || error.response?.data?.error || 'Failed to resend verification email. Please try again later.';
      alert(msg);
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      {!user.isEmailVerified && (
        <div className="bg-amber-500/10 border-b border-amber-500/20 py-2 px-4 text-center">
          <p className="text-xs font-medium text-amber-400 flex items-center justify-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5" />
            Your email is not verified.
            <Link to="/verify-email" className="underline hover:text-amber-300 ml-1 font-bold">Verify now with code</Link>
            <span className="mx-2 opacity-30">•</span>
            Check your inbox (including spam) or
            <button
              onClick={handleResendVerification}
              disabled={resending}
              className="underline hover:text-amber-300 ml-1 font-bold disabled:opacity-50"
            >
              {resending ? 'Resending...' : 'Resend link'}
            </button>
          </p>
        </div>
      )}
      <header className="sticky top-0 z-50 w-full border-b border-border bg-card text-foreground">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          {/* Left Side: Logo and Main Nav */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 mr-4">
              <Link to="/dashboard" className="flex items-center gap-3 group">
                <img src={brand.logo} alt={brand.name} className="h-8 w-auto transition-transform group-hover:scale-110" />
                <span className="text-xl font-brand tracking-tighter text-primary uppercase">
                  {brand.name}
                </span>
              </Link>
            </div>

            <nav className="hidden md:flex items-center gap-1">
              <Button
                onClick={() => openPostModal()}
                className="mr-4 flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-bold transition-colors"
              >
                <Plus className="h-4 w-4" />
                Post now
              </Button>

              <Button
                variant="ghost"
                asChild
                className={`flex items-center gap-2 px-3 ${isActive('/dashboard') ? 'text-primary font-bold bg-primary/10' : 'text-muted-foreground hover:text-primary hover:bg-primary/5'}`}
              >
                <Link to="/dashboard">
                  <LayoutDashboard className="h-4 w-4" />
                  Dashboard
                </Link>
              </Button>

              <Button
                variant="ghost"
                asChild
                className={`flex items-center gap-2 px-3 ${isActive('/posts') ? 'text-primary font-bold bg-primary/10' : 'text-muted-foreground hover:text-primary hover:bg-primary/5'}`}
              >
                <Link to="/posts">
                  <CalendarDays className="h-4 w-4" />
                  Posts & Agenda
                </Link>
              </Button>

              {user?.featureFlags?.store_videos ? (
                <Button
                  variant="ghost"
                  asChild
                  className={`flex items-center gap-2 px-3 ${isActive('/my-media') ? 'text-primary font-bold bg-primary/10' : 'text-muted-foreground hover:text-primary hover:bg-primary/5'}`}
                >
                  <Link to="/my-media">
                    <MediaIcon className="h-4 w-4" />
                    My Media
                  </Link>
                </Button>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-2 px-3 h-9 rounded-md text-sm font-medium text-muted-foreground opacity-50 cursor-not-allowed">
                        <MediaIcon className="h-4 w-4" />
                        My Media
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="bg-muted border-border text-foreground text-xs">
                      Only for Indie users
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </nav>
          </div>

          {/* Right Side: Actions and Profile */}
          <div className="flex items-center gap-4">
            {/* Game Selector */}
            {brand.features.showProjects && (
            <div className="w-48">
              <Select
                value={activeProject?.id || ""}
                onValueChange={(value: string) => {
                  if (value === "add_new") {
                    handleNavigate('/projects');
                    return;
                  }
                  const project = projects.find(p => p.id === value);
                  if (project) setActiveProject(project);
                }}
              >
                <SelectTrigger className="h-9 bg-background border-border text-foreground">
                  <SelectValue placeholder="Select Project" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border text-foreground">
                  {projects.length > 0 && (
                    <>
                      <DropdownMenuLabel className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Your Projects
                      </DropdownMenuLabel>
                      {projects.map((project) => (
                        <SelectItem key={project.id} value={project.id} className="cursor-pointer">
                          {project.name}
                        </SelectItem>
                      ))}
                      <DropdownMenuSeparator className="bg-muted my-1" />
                    </>
                  )}
                  <SelectItem value="add_new" className="cursor-pointer text-primary focus:text-primary/80 font-medium">
                    <div className="flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      Add New Project
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            )}

            {user?.tier === 'FREE' && (
              <Button
                variant="ghost"
                asChild
                className="hidden md:flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary to-indigo-600 text-white font-black rounded-xl shadow-lg shadow-primary/20 hover:from-primary/90 hover:to-indigo-700 hover:scale-105 transition-all animate-breathe"
              >
                <Link to="/pricing">
                  <Zap className="h-4 w-4 fill-white" />
                  Upgrade your account!
                </Link>
              </Button>
            )}

            {/* Notifications */}
            <NotificationBell />

            {/* User Profile Dropdown */}
            <div className="flex items-center gap-2">
              {brand.features.showGamification && (
              <UserRPGBadges
                user={{
                  ...user,
                  streakCount: activeProject?.streakCount || 0,
                  levelInfo: activeProject?.levelInfo
                }}
                size="sm"
                avatarUrl={`${API_BASE_URL}/users/${user.id}/avatar`}
              />
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="rounded-xl overflow-visible hover:bg-primary/10 p-1 h-auto min-h-[40px] px-2 transition-all">
                    {brand.features.showGamification ? (
                      <GamifiedAvatar
                        user={{
                          ...user,
                          levelInfo: activeProject?.levelInfo
                        }}
                        size="sm"
                        avatarUrl={`${API_BASE_URL}/users/${user.id}/avatar`}
                      />
                    ) : (
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={`${API_BASE_URL}/users/${user.id}/avatar`} />
                        <AvatarFallback className="text-xs bg-primary/20 text-primary">{getInitials(user.name || '')}</AvatarFallback>
                      </Avatar>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-card border-border text-foreground shadow-xl">
                  <DropdownMenuLabel className="flex items-center justify-between">
                    <span>My Account</span>
                    <Badge className={cn(
                      "text-[8px] px-1.5 h-4 border-none font-black tracking-widest",
                      user.tier === 'FREE' ? "bg-slate-500/20 text-muted-foreground" :
                        user.tier === 'BASIC' ? "bg-primary/20 text-primary" :
                          user.tier === 'PRO' ? "bg-purple-500/20 text-purple-400" :
                            "bg-amber-500/20 text-amber-400"
                    )}>
                      {user.tier === 'PRO' ? 'INDIE' : user.tier}
                    </Badge>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-muted" />
                  <DropdownMenuItem onClick={() => openSupport()} className="hover:bg-primary/10 cursor-pointer">
                    <HelpCircle className="mr-2 h-4 w-4" /> Support Center
                  </DropdownMenuItem>
                  {brand.features.showProjects && (
                  <DropdownMenuItem onClick={() => handleNavigate('/projects')} className="hover:bg-primary/10 cursor-pointer">
                    <FolderKanban className="mr-2 h-4 w-4" /> Projects
                  </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => handleNavigate('/social-connections')} className="hover:bg-primary/10 cursor-pointer">
                    <Link2 className="mr-2 h-4 w-4" /> Social connections
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleNavigate('/billing')} className="hover:bg-primary/10 cursor-pointer">
                    <CreditCard className="mr-2 h-4 w-4" /> Subscription & Orders
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleNavigate('/settings')} className="hover:bg-primary/10 cursor-pointer">
                    <SettingsIcon className="mr-2 h-4 w-4" /> Settings
                  </DropdownMenuItem>
                  {user.isAdmin && (
                    <>
                      <DropdownMenuSeparator className="bg-muted" />
                      <DropdownMenuItem onClick={() => handleNavigate('/admin')} className="text-primary hover:bg-primary/10 cursor-pointer focus:text-primary">
                        <ShieldCheck className="mr-2 h-4 w-4" /> Admin Panel
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator className="bg-muted" />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-400 hover:bg-red-500/10 cursor-pointer focus:text-red-400">
                    <LogOut className="mr-2 h-4 w-4" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>
    </>
  );
}
